import axios from 'axios'
import { API_OT } from '@/constants'

const base = () => API_OT.baseUrl

export default {
  // ─── Listado / Kanban ───────────────────────────────────────────────────────

  fetchOrdenesTrabajo({ commit, state }, { page = 1 } = {}) {
    commit('SET_LOADING', true)

    return axios.get(base() + API_OT.lista, {
      params: { page, per_page: state.pagination.perPage, ...state.filtros }
    })
      .then((res) => commit('SET_ITEMS', res.data))
      .finally(() => commit('SET_LOADING', false))
  },

  fetchKanban({ commit, state }) {
    commit('SET_LOADING', true)

    return axios.get(base() + API_OT.lista, {
      params: { vista: 'kanban', ...state.filtros }
    })
      .then((res) => commit('SET_ITEMS', res.data))
      .finally(() => commit('SET_LOADING', false))
  },

  // Usado desde fuera del módulo (preview/detalle de Ticket) para mostrar el
  // vínculo Ticket → OT sin tocar el listado/paginación propios de OT — por
  // eso no comete a state.items, solo devuelve los datos.
  buscarOtsPorTicket(_, ticketId) {
    if (!ticketId) return Promise.resolve([])

    return axios.get(base() + API_OT.lista, { params: { ticket_id: ticketId, per_page: 10 } })
      .then((res) => res.data.data || [])
  },

  // ─── CRUD ────────────────────────────────────────────────────────────────────

  crearOrdenTrabajo(_, payload) {
    return axios.post(base() + API_OT.lista, payload).then((res) => res.data.data)
  },

  fetchDetalle({ commit }, id) {
    commit('SET_DETALLE_LOADING', true)

    return axios.get(base() + API_OT.detail(id))
      .then((res) => commit('SET_DETALLE', res.data.data))
      .finally(() => commit('SET_DETALLE_LOADING', false))
  },

  actualizarOrdenTrabajo({ commit }, { id, payload }) {
    return axios.post(base() + API_OT.detail(id), payload)
      .then((res) => { commit('PATCH_ITEM_IN_LIST', res.data.data)

        return res.data.data })
  },

  eliminarOrdenTrabajo({ dispatch }, id) {
    return axios.delete(base() + API_OT.detail(id)).then(() => dispatch('fetchKanban'))
  },

  archivarOT({ commit }, id) {
    return axios.post(base() + API_OT.archivar(id))
      .then((res) => { commit('PATCH_ITEM_IN_LIST', res.data.data)

        return res.data.data })
  },

  desarchivarOT({ commit }, id) {
    return axios.post(base() + API_OT.desarchivar(id))
      .then((res) => { commit('PATCH_ITEM_IN_LIST', res.data.data)

        return res.data.data })
  },

  cambiarEstado({ commit }, { id, estado, detalle }) {
    return axios.post(base() + API_OT.cambiarEstado(id), { estado, detalle })
      .then((res) => { commit('PATCH_ITEM_IN_LIST', res.data.data)

        return res.data.data })
  },

  toggleSuscripcion(_, id) {
    return axios.post(base() + API_OT.suscripcion(id)).then((res) => res.data.suscrito)
  },

  reordenarKanban(_, payload) {
    return axios.post(base() + API_OT.kanbanOrden, payload)
  },

  // ─── Checklists / Tareas ─────────────────────────────────────────────────────

  crearChecklist(_, { otId, nombre }) {
    return axios.post(base() + API_OT.checklists(otId), { nombre }).then((res) => res.data.data)
  },

  eliminarChecklist(_, checklistId) {
    return axios.delete(base() + API_OT.checklist(checklistId))
  },

  crearTarea(_, { otId, payload }) {
    return axios.post(base() + API_OT.tareas(otId), payload).then((res) => res.data.data)
  },

  actualizarTarea(_, { tareaId, payload }) {
    return axios.post(base() + API_OT.tarea(tareaId), payload).then((res) => res.data.data)
  },

  eliminarTarea(_, tareaId) {
    return axios.delete(base() + API_OT.tarea(tareaId))
  },

  // ─── Hilo / Adjuntos ─────────────────────────────────────────────────────────

  validateFiles(_, files) {
    const form = new FormData()

    files.forEach((f) => form.append('files[]', f))

    return axios.post(base() + API_OT.validateFiles, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data.documentos)
  },

  crearMensaje(_, payload) {
    return axios.post(base() + API_OT.mensajes, payload).then((res) => res.data.data)
  },

  eliminarMensaje(_, id) {
    return axios.delete(base() + API_OT.mensaje(id))
  },

  // El endpoint es autenticado (Bearer token) — un window.open() directo
  // haría una navegación de navegador que NO lleva el header Authorization
  // (ese header lo agrega un interceptor sobre esta misma instancia de axios,
  // ver Vue.use(VueAxios, axios) en store/modules/auth.js) y devolvería 401.
  // Por eso siempre se pide como blob vía axios y se dispara la descarga
  // manualmente con un <a> temporal.
  descargarArchivo({ dispatch }, { id, nombre = 'archivo' }) {
    return dispatch('fetchArchivoBlob', id).then((blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = nombre
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    })
  },

  // Para previsualizar imágenes inline (<img>) sin exponer nunca una URL
  // pública del archivo — mismo endpoint protegido que la descarga.
  fetchArchivoBlob(_, archivoId) {
    return axios.get(base() + API_OT.archivoDownload(archivoId), { responseType: 'blob' })
      .then((res) => res.data)
  },

  renombrarArchivo(_, { id, nombre }) {
    return axios.post(base() + API_OT.archivoRenombrar(id), { nombre }).then((res) => res.data.data)
  },

  // ─── Usuarios asignables ─────────────────────────────────────────────────────

  fetchAreas({ commit }) {
    return axios.get(base() + API_OT.areas)
      .then((res) => commit('SET_AREAS', res.data.data))
  },

  fetchUsuariosAsignables({ commit }, q = '') {
    return axios.get(base() + API_OT.responsables, { params: { q } })
      .then((res) => commit('SET_USUARIOS_ASIGNABLES', res.data.data))
  },

  // ─── Clientes (filtro / vínculo) ──────────────────────────────────────────────
  // Reusa el buscador de clientes ya existente en el portal (mismo que usa
  // Tickets al crear un ticket), no es un endpoint propio de OT.

  buscarClientes(_, q = '') {
    return axios.post(base() + '/api/clientes/search', { search: q })
      .then((res) => res.data.clientes || [])
  },

  // ─── Servicios de un cliente (vínculo) ────────────────────────────────────────

  buscarServiciosCliente(_, { clienteId, q = '' }) {
    return axios.get(base() + API_OT.serviciosBuscar, { params: { cliente_id: clienteId, q } })
      .then((res) => res.data.data || [])
  },

  // ─── Tickets (vínculo) ─────────────────────────────────────────────────────────
  // Reusa el listado/búsqueda de Tickets ya existente (getAllTickets). Soporta
  // filtro por número exacto (id) y/o por cliente (nombre/email/rut, ambos
  // combinables) — así, con un cliente ya elegido, se listan sus tickets sin
  // necesidad de escribir nada, y escribir un número los acota más.
  buscarTickets(_, { q = '', clienteRut = null } = {}) {
    const params = {}

    if (q && /^\d+$/.test(q)) params.id = q
    if (clienteRut) params.cliente = clienteRut
    if (!params.id && !params.cliente) return Promise.resolve([])

    return axios.get(base() + '/api/ticketsTable/getAllTickets', { params })
      .then((res) => res.data?.tickets?.data || [])
  },

  // ─── Plantillas de checklist ─────────────────────────────────────────────────
  // Listas de tareas predefinidas y con nombre (ej. "Habilitación VPS") que se
  // aplican al crear una OT en vez de escribir las mismas tareas a mano cada vez.

  fetchChecklistPlantillas({ commit }) {
    return axios.get(base() + API_OT.checklistPlantillas)
      .then((res) => commit('SET_CHECKLIST_PLANTILLAS', res.data.data))
  },

  crearChecklistPlantilla({ commit }, payload) {
    return axios.post(base() + API_OT.checklistPlantillas, payload)
      .then((res) => { commit('ADD_CHECKLIST_PLANTILLA', res.data.data)

        return res.data.data })
  },

  actualizarChecklistPlantilla({ commit }, { id, payload }) {
    return axios.post(base() + API_OT.checklistPlantilla(id), payload)
      .then((res) => { commit('PATCH_CHECKLIST_PLANTILLA', res.data.data)

        return res.data.data })
  },

  eliminarChecklistPlantilla({ commit }, id) {
    return axios.delete(base() + API_OT.checklistPlantilla(id))
      .then(() => commit('REMOVE_CHECKLIST_PLANTILLA', id))
  },

  // Crea una plantilla nueva a partir del checklist actual de la OT indicada.
  crearPlantillaDesdeChecklist({ commit }, { otId, nombre }) {
    return axios.post(base() + API_OT.checklistGuardarPlantilla(otId), { nombre })
      .then((res) => { commit('ADD_CHECKLIST_PLANTILLA', res.data.data)

        return res.data.data })
  },

  // Agrega los ítems de una plantilla como tareas nuevas al final del
  // checklist de la OT, en una sola request (el backend resuelve las tareas
  // desde la plantilla por id, no hace falta mandarlas).
  aplicarChecklistPlantilla(_, { otId, plantillaId, fechaLimite }) {
    return axios.post(base() + API_OT.checklistAplicarPlantilla(otId), { plantilla_id: plantillaId, fecha_limite: fechaLimite })
      .then((res) => res.data.data)
  }
}
